<script setup lang="ts">
import { computed } from 'vue';
import { scheduleBadges } from '@/components/arrayColumns/scheduleCell';
import { withPendingScheduleWrites } from '@/composables/api/scheduleItems';
import { useOpenContainerSection } from '@/composables/useOpenContainerSection';
import { COUNT_BUTTON_CLASS } from './countButton';
import ScheduleBadgePair from './ScheduleBadgePair.vue';
import { asScheduleRows } from './scheduleGroups';

/**
 * A custom list's schedule counts on its search card, as one button.
 *
 * The first two counts are always drawn, as 0 when empty, so they sit in the
 * same place on every card, even on a list that says "Not scheduled".
 *
 * They count every schedule, expired ones included, so they match the
 * schedules the card lists under them.
 *
 * Clicking the counts opens the list's inspector on its Schedules section
 * ({@link useOpenContainerSection}).
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /** The card holding the counts opens another record, so the counts open the list's inspector themselves. */
        opensOwnInspector?: boolean;
    }>(),
    { opensOwnInspector: false },
);

// Read through this browser's pending writes, so a save shows before the reindex lands.
const badges = computed(() =>
    scheduleBadges(
        asScheduleRows(withPendingScheduleWrites(props.row.id, props.row.location_zone_custom_playlist_schedules)) as Record<
            string,
            unknown
        >[],
        props.row,
    ),
);

const openSection = useOpenContainerSection(() => props.opensOwnInspector);

function openSchedules(event: MouseEvent) {
    openSection(event, 'LocationZoneCustomPlaylist', props.row.id, 'schedules');
}
</script>

<template>
    <!-- keydown.stop: the card cancels Enter and Space to open itself, which would also cancel this button's click.
         data-row-click-opens: the card ignores clicks on its buttons, but this click must go on to open the card. -->
    <button type="button" :class="COUNT_BUTTON_CLASS" data-testid="card-schedule-counts" data-row-click-opens @click="openSchedules" @keydown.stop>
        <ScheduleBadgePair :badges="badges" layout="joined" />
    </button>
</template>
